import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { IndianRupee } from "lucide-react";

const vehicleModels = [
  "Kinetic Safar Smart",
  "Kinetic Safar Jumbo",
  "Kinetic Safar Star",
  "Kinetic Safar Shakti",
  "Kinetic E-Luna",
  "Kinetic Zing HSS",
];

const vehicleColors = [
  "White", "Red", "Blue", "Green", "Yellow", "Black", "Silver", "Orange"
];

const paymentModes = [
  "Cash",
  "Bank Transfer / NEFT / RTGS",
  "Cheque",
  "UPI",
  "Finance / Loan",
  "Part Cash + Finance",
];

const financierOptions = [
  "HDFC Bank",
  "Sriram Finance",
  "Chola Finance",
  "IDFC First",
  "Other",
];

// ─── Helpers defined OUTSIDE the component so React doesn't remount inputs ───

/** Allow only digits and one decimal point in price/charge fields */
const handlePriceKeyDown = (e) => {
  const ctrl = e.ctrlKey || e.metaKey;
  if (ctrl) return; // Allow Ctrl+A, Ctrl+C, Ctrl+V, etc.
  const passKeys = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End"];
  if (passKeys.includes(e.key)) return;
  // Allow one decimal point
  if (e.key === "." && !e.target.value.includes(".")) return;
  // Allow digits 0-9
  if (/^\d$/.test(e.key)) return;
  e.preventDefault();
};

/** Allow only digits (whole numbers) */
const handleDigitsKeyDown = (e) => {
  const ctrl = e.ctrlKey || e.metaKey;
  if (ctrl) return;
  const passKeys = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End"];
  if (passKeys.includes(e.key)) return;
  if (/^\d$/.test(e.key)) return;
  e.preventDefault();
};

/** Aadhaar onChange — strips non-digits, caps at 12, formats as XXXX-XXXX-XXXX */
const handleAadhaarChange = (e, onInputChange) => {
  const raw = e.target.value.replace(/\D/g, "").slice(0, 12);
  let formatted = raw;
  if (raw.length > 8) formatted = `${raw.slice(0, 4)}-${raw.slice(4, 8)}-${raw.slice(8)}`;
  else if (raw.length > 4) formatted = `${raw.slice(0, 4)}-${raw.slice(4)}`;
  onInputChange({ target: { name: e.target.name, value: formatted } });
};

/** Phone onChange — only digits, max 10 */
const handlePhoneChange = (e, onInputChange) => {
  const raw = e.target.value.replace(/\D/g, "").slice(0, 10);
  onInputChange({ target: { name: e.target.name, value: raw } });
};

/** PAN onChange — uppercase, alphanumeric, max 10 chars */
const handlePanChange = (e, onInputChange) => {
  const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
  onInputChange({ target: { name: e.target.name, value: raw } });
};

// ─────────────────────────────────────────────────────────────────────────────

const InvoiceForm = ({ formData, onInputChange, onSelectChange, calculateTotal, computedCgst, computedSgst }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN').format(value || 0);
  };

  const isFinanceMode =
    formData.paymentMode === "Finance / Loan" ||
    formData.paymentMode === "Part Cash + Finance";

  const hasInsurance = parseFloat(formData.insuranceCharges) > 0;

  const isBatteryExcluded = formData.batteryIncluded === "No — Without Battery";

  return (
    <div className="space-y-8">
      {/* Document Info */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Invoice Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="invoiceNumber">Invoice Number</Label>
            <Input
              id="invoiceNumber"
              name="invoiceNumber"
              value={formData.invoiceNumber}
              onChange={onInputChange}
              className="bg-secondary/30"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invoiceDate">Invoice Date</Label>
            <Input
              id="invoiceDate"
              name="invoiceDate"
              type="date"
              value={formData.invoiceDate}
              onChange={onInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="deliveryDate">Delivery Date</Label>
            <Input
              id="deliveryDate"
              name="deliveryDate"
              type="date"
              value={formData.deliveryDate}
              onChange={onInputChange}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Customer Info */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Customer Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="customerName">Customer Name *</Label>
            <Input
              id="customerName"
              name="customerName"
              value={formData.customerName}
              onChange={onInputChange}
              placeholder="Enter customer name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="customerPhone">Phone Number * (10 digits)</Label>
            <Input
              id="customerPhone"
              name="customerPhone"
              value={formData.customerPhone}
              onChange={(e) => handlePhoneChange(e, onInputChange)}
              onKeyDown={handleDigitsKeyDown}
              placeholder="10-digit mobile number"
              maxLength={10}
              inputMode="numeric"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="customerAddress">Address *</Label>
            <Textarea
              id="customerAddress"
              name="customerAddress"
              value={formData.customerAddress}
              onChange={onInputChange}
              placeholder="Enter full address"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="customerAadhaar">Aadhaar Number (XXXX-XXXX-XXXX)</Label>
            <Input
              id="customerAadhaar"
              name="customerAadhaar"
              value={formData.customerAadhaar}
              onChange={(e) => handleAadhaarChange(e, onInputChange)}
              placeholder="XXXX-XXXX-XXXX"
              maxLength={14}
              inputMode="numeric"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="customerPan">PAN Card Number * (ABCDE1234F)</Label>
            <Input
              id="customerPan"
              name="customerPan"
              value={formData.customerPan}
              onChange={(e) => handlePanChange(e, onInputChange)}
              placeholder="ABCDE1234F"
              maxLength={10}
              className="uppercase"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="customerGstin">GSTIN (If applicable)</Label>
            <Input
              id="customerGstin"
              name="customerGstin"
              value={formData.customerGstin}
              onChange={onInputChange}
              placeholder="Enter GSTIN"
              className="uppercase"
              maxLength={15}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Vehicle Info */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Vehicle Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="vehicleModel">Vehicle Model *</Label>
            <Select
              value={formData.vehicleModel}
              onValueChange={(value) => onSelectChange('vehicleModel', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                {vehicleModels.map((model) => (
                  <SelectItem key={model} value={model}>{model}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="vehicleVariant">Variant</Label>
            <Input
              id="vehicleVariant"
              name="vehicleVariant"
              value={formData.vehicleVariant}
              onChange={onInputChange}
              placeholder="Enter variant"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vehicleColor">Color *</Label>
            <Select
              value={formData.vehicleColor}
              onValueChange={(value) => onSelectChange('vehicleColor', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select color" />
              </SelectTrigger>
              <SelectContent>
                {vehicleColors.map((color) => (
                  <SelectItem key={color} value={color}>{color}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="chassisNumber">Chassis Number *</Label>
            <Input
              id="chassisNumber"
              name="chassisNumber"
              value={formData.chassisNumber}
              onChange={onInputChange}
              placeholder="Enter chassis number"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="motorNumber">Motor Number *</Label>
            <Input
              id="motorNumber"
              name="motorNumber"
              value={formData.motorNumber}
              onChange={onInputChange}
              placeholder="Enter motor number"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="batteryMake">Battery Make</Label>
            <Input
              id="batteryMake"
              name="batteryMake"
              value={formData.batteryMake}
              onChange={onInputChange}
              placeholder="E.g., Exide, Okaya"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="batteryAh">Battery Capacity (Ah)</Label>
            <Input
              id="batteryAh"
              name="batteryAh"
              value={formData.batteryAh}
              onChange={onInputChange}
              placeholder="E.g., 100Ah"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="batteryIncluded">Battery Included?</Label>
            <Select
              value={formData.batteryIncluded}
              onValueChange={(value) => onSelectChange('batteryIncluded', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select battery status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes — With Battery">Yes — With Battery</SelectItem>
                <SelectItem value="No — Without Battery">No — Without Battery</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="batteryNumber">Battery Number</Label>
            <Input
              id="batteryNumber"
              name="batteryNumber"
              value={formData.batteryNumber}
              onChange={onInputChange}
              placeholder="Enter battery serial number (or N/A if no battery)"
              readOnly={isBatteryExcluded}
              className={isBatteryExcluded ? "bg-secondary/50 text-muted-foreground" : ""}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="chargerDetails">Charger Details</Label>
            <Input
              id="chargerDetails"
              name="chargerDetails"
              value={formData.chargerDetails}
              onChange={onInputChange}
              placeholder="E.g., 60V 15A"
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Pricing */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Pricing &amp; Tax Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="exShowroomPrice">Ex-Showroom Price *</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="exShowroomPrice"
                name="exShowroomPrice"
                value={formData.exShowroomPrice}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9"
                inputMode="decimal"
              />
            </div>
          </div>

          {/* CGST — auto-calculated, read-only */}
          <div className="space-y-2">
            <Label htmlFor="cgst">
              CGST @ 2.5% <span className="text-xs text-muted-foreground">(Auto-calculated)</span>
            </Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="cgst"
                value={formatCurrency(computedCgst)}
                readOnly
                className="pl-9 bg-secondary/50 text-muted-foreground cursor-not-allowed"
                tabIndex={-1}
              />
            </div>
          </div>

          {/* SGST — auto-calculated, read-only */}
          <div className="space-y-2">
            <Label htmlFor="sgst">
              SGST @ 2.5% <span className="text-xs text-muted-foreground">(Auto-calculated)</span>
            </Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="sgst"
                value={formatCurrency(computedSgst)}
                readOnly
                className="pl-9 bg-secondary/50 text-muted-foreground cursor-not-allowed"
                tabIndex={-1}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rtoCharges">RTO Charges</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="rtoCharges"
                name="rtoCharges"
                value={formData.rtoCharges}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="insuranceCharges">Insurance</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="insuranceCharges"
                name="insuranceCharges"
                value={formData.insuranceCharges}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="accessoriesCharges">Accessories</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="accessoriesCharges"
                name="accessoriesCharges"
                value={formData.accessoriesCharges}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="otherCharges">Other Charges</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="otherCharges"
                name="otherCharges"
                value={formData.otherCharges}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="discount">Discount</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="discount"
                name="discount"
                value={formData.discount}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9"
                inputMode="decimal"
              />
            </div>
          </div>
        </div>

        {/* Total */}
        <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center justify-between">
            <span className="font-heading font-semibold text-foreground">Grand Total</span>
            <span className="text-2xl font-bold text-primary">
              ₹{formatCurrency(calculateTotal())}
            </span>
          </div>
        </div>
      </div>

      <Separator />

      {/* Payment Info */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Payment Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="paymentMode">Payment Mode *</Label>
            <Select
              value={formData.paymentMode}
              onValueChange={(value) => onSelectChange('paymentMode', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select payment mode" />
              </SelectTrigger>
              <SelectContent>
                {paymentModes.map((mode) => (
                  <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="paymentReference">Payment Reference / Transaction ID</Label>
            <Input
              id="paymentReference"
              name="paymentReference"
              value={formData.paymentReference}
              onChange={onInputChange}
              placeholder="Cheque no. / UTR / Transaction ID"
            />
          </div>

          {/* Financier Name — shown only for Finance modes */}
          {isFinanceMode && (
            <>
              <div className="space-y-2">
                <Label htmlFor="financierName">Financier Name</Label>
                <Select
                  value={formData.financierName}
                  onValueChange={(value) => onSelectChange('financierName', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select financier" />
                  </SelectTrigger>
                  <SelectContent>
                    {financierOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.financierName === "Other" && (
                <div className="space-y-2">
                  <Label htmlFor="financierNameCustom">Custom Financier Name</Label>
                  <Input
                    id="financierNameCustom"
                    name="financierNameCustom"
                    value={formData.financierNameCustom}
                    onChange={onInputChange}
                    placeholder="Enter financier name"
                  />
                </div>
              )}
            </>
          )}

          {/* Insurance Company — shown only when insurance charges > 0 */}
          {hasInsurance && (
            <div className="space-y-2">
              <Label htmlFor="insuranceCompany">Insurance Company</Label>
              <Input
                id="insuranceCompany"
                name="insuranceCompany"
                value={formData.insuranceCompany}
                onChange={onInputChange}
                placeholder="E.g., National Insurance, New India Assurance"
              />
            </div>
          )}

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={onInputChange}
              placeholder="Warranty details, delivery notes, etc."
              rows={3}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceForm;
